import { IAvailableSlots } from '@/types';
import { AvailableSlotsDTO, CustomerDetails, IVehicleInfo } from './types';
import { format, parseISO, setHours } from 'date-fns';
import { geocodeByLatLng } from 'react-google-places-autocomplete';
import Cookies from 'js-cookie';
import { getUrlWithUtmParams } from '../common/router';
import {
    CarMakeImagesMap,
    fuelTypeEnum,
    GARAGE_ID_MAP,
    GARAGE_MAPPING,
    ROUTEMAP,
} from './constants';
//import Router from '@/components/common/router';
import { registrationNumberRegex } from '@/constants/common';
import { authenticationService } from '@/services/authentication_service';
import { getUserIdFromCookie } from '@/utils/common';

export const mapAvailableSlots = (
    availableSlots: AvailableSlotsDTO[]
): IAvailableSlots => {
    return availableSlots.map((availableSlot) => {
        const sortedSlots = availableSlot.slots.sort((a, b) => {
            return (
                new Date(a.start_time).getTime() -
                new Date(b.start_time).getTime()
            );
        });

        return {
            ...availableSlot,
            slots: sortedSlots.map((slot) => {
                return {
                    id: slot.slot_id != null ? String(slot.slot_id) : null,
                    startTime: slot.start_time,
                    endTime: slot.end_time,
                    count: slot.total_capacity,
                    isSameDayService: slot.same_day_service,
                    disabled: slot.disabled,
                };
            }),
        };
    });
};

export function formatDisplayTime(startTime: string, endTime: string): string {
    if (!startTime || !endTime) {
        return '';
    }
    const start = parseISO(startTime);
    const end = parseISO(endTime);

    const adjustedStartDate = start;
    const adjustedEndDate = end;

    const displayTime = `${format(adjustedStartDate, 'h:mm a')} - ${format(
        adjustedEndDate,
        'h:mm a'
    )}`;

    return displayTime;
}

export const fetchCityFromCoordinates = async (
    position: any
): Promise<{ city: string | null; pincode: string | null }> => {
    try {
        const results = await geocodeByLatLng({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
        });
        console.log(results);
        const cityComponent = results[0].address_components.find((comp: any) =>
            comp.types.includes('locality')
        );

        const pincodeComponent = results[0].address_components.find(
            (comp: any) => comp.types.includes('postal_code')
        );

        return {
            city: cityComponent ? cityComponent.long_name : null,
            pincode: pincodeComponent ? pincodeComponent.long_name : null,
        };
    } catch (error) {
        console.error('Error fetching address from coordinates:', error);
        return { city: null, pincode: null };
    }
};

export const generateMmvData = (carDetails: IVehicleInfo) => {
    const {
        acko_model_id,
        acko_make_id,
        model_name,
        make_name,
        fuel_type,
        registration_year,
        color,
        acko_variant_id,
        variant_name,
    } = carDetails;
    const carAge = new Date().getFullYear() - Number(registration_year);

    return {
        model_id: acko_model_id,
        model: model_name,
        make_id: acko_make_id,
        make: make_name,
        fuel_type: fuel_type?.toUpperCase(),
        car_age: carAge,
        color: color || null,
        variant_id: acko_variant_id,
        variant: variant_name,
    };
};

export function convertPricing(data: any) {
    const essential = data.package_pricing.find(
        (item: any) => item.name === 'ESSENTIAL'
    );
    const enhanced = data.package_pricing.find(
        (item: any) => item.name === 'ENHANCED'
    );

    const basicPrice = {
        ackoBasicPrice: essential.offer_price,
        ackoOldBasicPrice: essential.amount,
        dealershipBasicPrice: essential.other_garage_amount,
    };

    const advancedPrice = {
        ackoAdvancedPrice: enhanced.offer_price,
        ackoOldAdvancedPrice: enhanced.amount,
        dealershipAdvancedPrice: enhanced.other_garage_amount,
    };

    return {
        discount: data.standard_discount,
        basicPrice,
        advancedPrice,
    };
}

export const getObjFromSession = (key: string) => {
    // @ts-ignore
    return JSON.parse(sessionStorage.getItem(key));
};
export const getObjFromLocal = (key: string) => {
    // @ts-ignore
    return JSON.parse(localStorage.getItem(key));
};

export function redirectToLoginIfNoUserId(route: string, Router: any): void {
    if (!getUserIdFromCookie()) {
        window.location.href = getUrlWithUtmParams({
            url: `/login`,
            queryParams: {
                next: `${process.env.NEXT_PUBLIC_ENV_PREFIX}${route}`,
            },
        });
    } else {
        Router.push(`${route}`);
        //return;
    }
}

export const formatGarageData = (garageData: any) => {
    const transformedData = {
        garageId: garageData?.garageId || '',
        garageAddress: garageData?.garage_address,
        garageBannerImage: {
            caption: garageData?.garage_banner_image?.data?.attributes?.caption,
            imageUrl:
                garageData?.garage_banner_image?.data?.attributes?.formats
                    ?.medium?.url ||
                garageData?.garage_banner_image?.data?.attributes?.url ||
                null,
            imageThumbnail:
                garageData?.garage_banner_image?.data?.attributes?.formats
                    ?.thumbnail?.url || null,
        },
        servicingBrands: garageData?.servicing_brands.map((company: any) => ({
            id: company?.id,
            name: company?.company_name,
            url: company?.company_image_url?.data.attributes.url,
        })),
        partSupplierBrands: garageData?.parts_supplier.map((company: any) => ({
            id: company?.id,
            name: company?.company_name,
            url: company?.company_img_url.data.attributes.url,
        })),
        garageReviewRatings: garageData?.garage_review_ratings || {},
        garageNumber: garageData?.garage_phone_number,
        garageVideo: {
            caption: '',
            imageUrl: garageData?.garage_video,
        },
        garageTitle: garageData?.meta_title || '',
        garageKeywords: garageData?.meta_keywords,
        garageDescription: garageData?.meta_description || '',
        garageGallery: garageData?.garage_gallery?.data.map((image: any) => ({
            id: image.id,
            imageUrl: image.attributes.url,
            imageName: image.attributes.name,
        })),
        locationInfo: garageData?.location_info || null,
        serviceableAreas: garageData?.servicable_areas?.areas || [],
        //To do- This needs to be passed from strapi
        garageDirectionUrl:
            'https://www.google.com/maps/dir/13.0843007,80.2704622/48%2F4B%26C,+Garvebahvipalya,+ACKO+Drive+Service+Centre,+near,+7th+Mile+Hosur+Rd,+near+Kudlu+Gate,+Bengaluru,+Karnataka+560068/@12.7825616,76.3134289,7z/data=!3m1!4b1!4m9!4m8!1m1!4e1!1m5!1m1!1s0x3bae15cd1fa289d7:0xee396fec6fd83e2e!2m2!1d77.6418947!2d12.8898342?entry=ttu',
        garageTiming: {
            openTiming: 'Mon - Fri : 10:00 am - 08:00 pm',
            closeTiming: 'Sat & Sun : 10:00 am - 06:00 pm',
        },
        garageOffers: formatOffers(garageData.offers || []),
    };
    return transformedData;
};

const formatOffers = (offers: any) => {
    if (offers.length === 0) return [];
    //TO do:this needs to be standardized for now its a workaround
    const data = offers.map((offer: any) => ({
        text: `${offer.discount_percentage}% OFF`,
        details: offer?.title || '',
        imgSrc: offer?.image?.data?.attributes?.url,
    }));
    // [
    //     {
    //         text: '20% OFF',
    //         details: ['on CAR WASH ', 'for new users'],
    //         imgSrc: '/images/offer-car-card.svg',
    //     },
    //     {
    //         text: '20% OFF',
    //         details: [' on PERIODIC SERVICE ', 'for new users'],
    //         imgSrc: '/images/offer-card2.svg',
    //         bg: 'blue-bg',
    //     },
    //     {
    //         text: '20% OFF',
    //         details: [' on CAR WASH ', 'for new users'],
    //         imgSrc: '/images/offer-card3.svg',
    //         bg: 'red-bg',
    //     },
    // ];
    console.log('formatOffers', data);

    return data;
};

export const mapperStrapiGarageId = (garageId?: string): string | undefined => {
    if (!garageId) {
        console.warn('No garageId provided');
        return undefined;
    }

    const mappedGarage = GARAGE_ID_MAP[garageId];

    if (mappedGarage === undefined) {
        console.warn(`Garage ID: ${garageId} is not mapped`);
        return undefined;
    }

    return mappedGarage;
};

export const getMMVDetails = () => {
    const mmv = getObjFromLocal('mmv');
    return {
        model_id:
            mmv?.model_id != null && typeof mmv?.model_id !== 'number'
                ? Number(mmv?.model_id)
                : mmv?.model_id,
        vehicle_make: mmv?.make || '',
        vehicle_model: mmv?.model || '',
        variant: '',
        fuel_type: mmv?.fuel_type || '',
        car_age:
            mmv?.car_age != null && typeof mmv?.car_age !== 'number'
                ? Number(mmv?.car_age)
                : mmv?.car_age,
    };
};

export const formatBookingDetails = (booking: any) => {
    return {
        adsc_slot_pincode: booking?.pickup_details?.address?.pincode || '',
        adsc_slot_date: booking?.booking_details?.appointment_date || '',
        adsc_slot_time:
            formatDisplayTime(
                booking?.booking_details?.slot_details?.start_time,
                booking?.booking_details?.slot_details?.end_time
            ) || '',
        same_day_delivery:
            booking?.booking_details?.slot_details?.same_day_service,
        booking_fee:
            booking?.payment_details?.amount != null &&
            typeof booking?.payment_details?.amount !== 'string'
                ? String(booking?.payment_details?.amount)
                : booking?.payment_details?.amount || '',
    };
};
export const getGarageUrlName = (garageId: string): string =>
    GARAGE_MAPPING[garageId]?.urlName || '';
export const getGarageDisplayName = (garageId: string): string =>
    GARAGE_MAPPING[garageId]?.displayName || '';
export const getGarageRoutePath = (garageId: string): string =>
    GARAGE_MAPPING[garageId]?.routePath || ROUTEMAP.GARAGE_COMING_SOON;

export const getGarageIdFromUrlName = (urlName: string): string | undefined => {
    return Object.entries(GARAGE_MAPPING).find(
        ([_, value]) => value.urlName === urlName
    )?.[0];
};

export const getAvailabilityText = (slotData: IAvailableSlots): string => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const tomorrowDate = tomorrow.toISOString().split('T')[0];
    const weekEndDate = new Date(today);
    weekEndDate.setDate(weekEndDate.getDate() + 7);

    const tomorrowSlots =
        slotData
            .find((day) => day.date === tomorrowDate)
            ?.slots.reduce((accum, curr) => curr?.count + accum, 0) || 0;
    console.log({ tomorrowSlots });

    const weekSlots = slotData.reduce((total, day) => {
        const dayDate = new Date(day.date);
        if (dayDate > today && dayDate <= weekEndDate) {
            return day.slots.reduce((accum, curr) => curr.count + accum, total);
        }
        return total;
    }, 0);

    if (tomorrowSlots >= 2) {
        return `We've got ${tomorrowSlots} slots available tomorrow and ${weekSlots} slots available this week for you to choose from`;
    } else if (weekSlots > 2) {
        return `We've ${weekSlots} slots available this week for you to choose from`;
    } else {
        return "We've got multiple slots available for you to choose from";
    }
};

export const getIconFromMake = (make: string | undefined) => {
    if (make === undefined) return '';
    return CarMakeImagesMap[make] || '';
};

export function transformAddress(data: any) {
    // Split the description to extract city and state information
    const descriptionParts = data?.description.split(', ');
    const city = descriptionParts?.[descriptionParts.length - 3];
    const state = descriptionParts?.[descriptionParts.length - 2];
    const pincode = data?.pincode;
    const main_text = data?.main_text || '';
    const description = data?.description || '';
    // const line = main_text + ',' + description;
    const houseNumber = data?.house_number || '';
    const locality = data?.locality || '';
    const latitude = data?.lat || '';
    const longitude = data?.lon || '';

    return {
        line: main_text,
        description: description,
        city: city || '',
        state: state || '',
        pincode: pincode || '',
        house_number: houseNumber,
        locality: locality,
        location_coords: {
            latitude: latitude,
            longitude: longitude,
        },
        // instructions: data?.instruction || '',
        // alternate_phone: data?.alt_ph_number || '',
    };
}

export const convertMMVPayload = (mmv: any, number: string) => {
    const fuelType = fuelTypeEnum.find(
        (type) => type.name.toLowerCase() === mmv.fuel_type
    );

    return {
        fuel_type: fuelType?.id || null,
        registration_number: number || null,
        ...mmv,
    };
};

export const mapUserDataToCustomerDetails = (
    userData: any
): CustomerDetails => {
    //To-do remove the hardcoded value before merging to master
    return {
        email_id: userData.email || '',
        full_name: userData.userName || '',
        phone_number: userData.phoneNumber || '',
    };
};

export const formatPath = (city: string, displayName: string): string => {
    return `${city?.toLowerCase()}/${displayName
        ?.toLowerCase()
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/[^a-z0-9-]/g, '')}`; // Remove special characters
};

export const getHomePageUrl = (garageId: string) => {
    const landingPagePath = getGarageRoutePath(garageId as string);
    const redirectUrl = getUrlWithUtmParams({
        url: `${process.env.NEXT_PUBLIC_ENV_PREFIX}${landingPagePath}`,
        queryParams: {},
    });

    return redirectUrl;
};

type MappedVehicle = {
    vehicle_make: string;
    vehicle_model: string;
    model_id: number;
    car_age: number;
};

export function getMappedVehicleFromLocalStorage(): MappedVehicle {
    const storedData = localStorage.getItem('mmv');

    const vehicle = storedData ? JSON.parse(storedData) : null;

    return {
        vehicle_make: vehicle?.make || '',
        vehicle_model: vehicle?.model || '',
        model_id:
            vehicle?.model_id != null && typeof vehicle?.model_id !== 'number'
                ? Number(vehicle?.model_id)
                : vehicle?.model_id,
        car_age: vehicle?.car_age || 0,
    };
}

export const getPlatform = (): string => {
    const isWebView = Cookies.get('webview')
        ? `${Cookies.get('webview')}_webview`
        : '';
    //Note:-This function is taken from Claims FNOL
    const isMWeb = (): boolean => {
        return (
            !isWebView &&
            (Math.min(window.screen.width, window.screen.height) < 700 ||
                'ontouchstart' in document.documentElement ||
                navigator.userAgent.indexOf('Mobi') > -1)
        );
    };

    return isWebView || `${isMWeb() ? 'mW' : 'w'}eb`;
};

export const appendCurrentParamsToRoute = (route: string): string => {
    const currentParams = new URLSearchParams(window.location.search);

    const [baseWithQuery, hash] = route.split('#');
    const [baseRoute, existingQuery] = baseWithQuery.split('?');

    const finalParams = new URLSearchParams(existingQuery || '');
    currentParams.forEach((value, key) => finalParams.set(key, value));

    const queryString = finalParams.toString();
    const hashFragment = hash ? `#${hash}` : '';
    return `${baseRoute}?${queryString}${hashFragment}`;
};

export const getUniqueObjects = <T>(array: T[], key: keyof T): T[] => {
    return Array.from(new Map(array.map((item) => [item[key], item])).values());
};
